import { ArrowSquareOut } from '@phosphor-icons/react';
import { NavLink } from 'react-router-dom';

import { useAuth } from '@/lib/auth/AuthContext';
import type { ConsoleRole } from '@/lib/auth/types';
import { useEnvStore } from '@/lib/store/envStore';
import { cn } from '@/lib/utils/cn';
import { Badge } from '@/ui/badge';

import { NAV_SECTIONS, type NavItem, type NavSection } from './navItems';

interface SidebarProps {
    onNavigate?: () => void;
}

function canSee(role: ConsoleRole | undefined, item: NavItem): boolean {
    if (!item.roles) return true;
    if (!role) return false;
    return item.roles.includes(role);
}

/**
 * Sidebar navigation — text-only, restraint deliberate (cf. F3 plan §2.1).
 * Roles filter what the current user sees. Backend remains source of truth.
 */
export function Sidebar({ onNavigate }: SidebarProps) {
    const { user } = useAuth();
    const env = useEnvStore((state) => state.env);
    const role = user?.role;

    return (
        <nav
            aria-label="Console navigation"
            className="flex h-full flex-col gap-1 overflow-y-auto px-3 py-4"
        >
            <div className="flex items-start justify-between gap-2 px-2 pb-3">
                <div className="flex items-center gap-2">
                    <img src="/safariat-logo.svg" alt="Safariat" className="h-7 w-7" />
                    <div>
                        <p className="font-display text-base font-bold tracking-tight">SafarAPI</p>
                        <p className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">
                            Console
                        </p>
                    </div>
                </div>
                {env === 'live' ? (
                    <Badge tone="destructive" aria-label="Live environment active">
                        LIVE
                    </Badge>
                ) : null}
            </div>

            {NAV_SECTIONS.map((section) => (
                <SidebarSection
                    key={section.id}
                    section={section}
                    role={role}
                    onNavigate={onNavigate}
                />
            ))}
        </nav>
    );
}

interface SidebarSectionProps {
    section: NavSection;
    role: ConsoleRole | undefined;
    onNavigate?: () => void;
}

function SidebarSection({ section, role, onNavigate }: SidebarSectionProps) {
    const visibleItems = section.items.filter((item) => canSee(role, item));
    if (visibleItems.length === 0) return null;

    return (
        <div className="mt-1 flex flex-col gap-0.5">
            {section.label && (
                <p className="px-3 pb-1 pt-3 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {section.label}
                </p>
            )}
            {visibleItems.map((item) => (
                <SidebarLink key={item.to} item={item} onNavigate={onNavigate} />
            ))}
        </div>
    );
}

interface SidebarLinkProps {
    item: NavItem;
    onNavigate?: () => void;
}

function SidebarLink({ item, onNavigate }: SidebarLinkProps) {
    const baseClasses =
        'group flex h-8 items-center justify-between rounded px-3 text-sm font-medium transition-colors duration-fast';

    if (item.external) {
        return (
            <a
                href={item.to}
                target="_blank"
                rel="noreferrer"
                className={cn(baseClasses, 'text-muted-foreground hover:bg-muted hover:text-foreground')}
                onClick={onNavigate}
            >
                <span>{item.label}</span>
                <ArrowSquareOut
                    aria-hidden
                    weight="regular"
                    className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                />
            </a>
        );
    }

    return (
        <NavLink
            to={item.to}
            end={item.to === '/'}
            onClick={onNavigate}
            className={({ isActive }) =>
                cn(
                    baseClasses,
                    isActive
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )
            }
        >
            <span>{item.label}</span>
        </NavLink>
    );
}
